import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  InputNumber,
  Switch,
  Button,
  message,
  Spin,
  Typography,
  Divider,
  Radio,
  Input
} from 'antd'
import api from '../utils/api'
import Hint from './common/Hint'
import {
  resolvePaymentFeeSettings,
  calcCustomerSurcharge
} from '../utils/paymentSurcharge'

const { Title, Paragraph, Text } = Typography

/**
 * 金流手續費設定
 *
 * 讓店家設定「刷卡」與「行動支付」的預設手續費率，
 * 結帳時會自動依金額 × 費率計算手續費（店家可單筆調整）。
 *
 * 會計概念：
 *  - 刷卡、行動支付屬「非現金」應收
 *  - 收單行／第三方支付於撥款時會扣除手續費
 *  - 手續費為「銷售費用 - 金流服務費」
 *  - 真實入帳金額 = 面額 − 手續費
 *  - 數據報表會將手續費從收入中扣除以顯示真實收益
 */
const PaymentFeeSettings = ({ businessId }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // 即時試算：讓店家在按下儲存之前，就先看到客人會被收多少
  const chargeServiceFee = Form.useWatch('chargeServiceFee', form)
  const wCardRate = Form.useWatch('cardFeeRate', form)
  const wMobileRate = Form.useWatch('mobilePaymentFeeRate', form)
  const wMode = Form.useWatch('feeBearerMode', form)
  const wRounding = Form.useWatch('feeRounding', form)
  const wApplyCard = Form.useWatch('applyToCard', form)
  const wApplyMobile = Form.useWatch('applyToMobile', form)
  const wLabel = Form.useWatch('feeLabel', form)

  // 用與結帳頁、後端完全相同的公式試算（src/utils/paymentSurcharge.js）
  const PREVIEW_BASE = 1000
  const previewSettings = resolvePaymentFeeSettings({
    paymentFeeSettings: {
      feeBearer: chargeServiceFee ? 'customer' : 'shop',
      cardFeeRate: wCardRate,
      mobilePaymentFeeRate: wMobileRate,
      feeBearerMode: wMode,
      feeRounding: wRounding,
      applyToCard: wApplyCard,
      applyToMobile: wApplyMobile,
      feeLabel: wLabel
    }
  })
  const previewCardFee = calcCustomerSurcharge({
    cardBase: PREVIEW_BASE,
    settings: previewSettings
  }).card
  const previewMobileFee = calcCustomerSurcharge({
    mobileBase: PREVIEW_BASE,
    settings: previewSettings
  }).mobile

  /**
   * 店家最後實際入袋多少。
   *
   * 這一行是整個設定頁最重要的數字 —— 銀行是對「客人實際刷的總額」抽成，
   * 所以單純把應收乘上費率，看起來收滿了，實際入帳還是會少一點
   * （少的正好是 應收 × 費率²，1,000 元差 0.78 元，金額越大差越多）。
   * 把實入印出來，店家才看得出兩種計算方式差在哪，不必先懂公式。
   */
  const netAfterBankFee = (fee, ratePct) => {
    const pays = PREVIEW_BASE + fee
    const net = pays - (pays * (Number(ratePct) || 0)) / 100
    // 只有在有小數時才顯示小數，避免 1,000.00 這種多餘的位數
    return Number.isInteger(net) ? net : net.toFixed(2)
  }
  const previewCardNet = netAfterBankFee(previewCardFee, wCardRate)
  const previewMobileNet = netAfterBankFee(previewMobileFee, wMobileRate)

  // 開了服務費卻沒選任何支付方式 = 設定不會生效，要講出來
  const enabledButNoTarget =
    chargeServiceFee === true && !wApplyCard && !wApplyMobile

  // 載入當前設定
  useEffect(() => {
    if (!businessId) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const res = await api.get(
          `/api/business_B/${businessId}/payment-fee-settings`
        )
        if (!cancelled && res?.data?.success) {
          const d = res.data.data
          form.setFieldsValue({
            cardFeeRate: d.cardFeeRate,
            mobilePaymentFeeRate: d.mobilePaymentFeeRate,
            autoCalculate: d.autoCalculate,
            // 表單用一個好懂的開關代表 feeBearer，送出時再轉回 shop / customer
            chargeServiceFee: d.feeBearer === 'customer',
            feeBearerMode: d.feeBearerMode || 'gross_up',
            feeRounding: d.feeRounding || 'ceil',
            feeLabel: d.feeLabel || '服務費',
            applyToCard: d.applyToCard === true,
            applyToMobile: d.applyToMobile === true
          })
        }
      } catch (err) {
        console.error('載入金流手續費設定失敗:', err)
        // 若 API 尚未部署，使用預設值
        form.setFieldsValue({
          cardFeeRate: 2.8,
          mobilePaymentFeeRate: 2.5,
          autoCalculate: true,
          chargeServiceFee: false,
          feeBearerMode: 'gross_up',
          feeRounding: 'ceil',
          feeLabel: '服務費',
          applyToCard: false,
          applyToMobile: false
        })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [businessId, form])

  const handleSave = async (values) => {
    setSaving(true)
    try {
      const payload = {
        cardFeeRate: values.cardFeeRate,
        mobilePaymentFeeRate: values.mobilePaymentFeeRate,
        autoCalculate: values.autoCalculate,
        // 表單的開關要轉回後端的 feeBearer 值
        feeBearer: values.chargeServiceFee ? 'customer' : 'shop',
        feeBearerMode: values.feeBearerMode || 'gross_up',
        feeRounding: values.feeRounding || 'ceil',
        feeLabel: values.feeLabel || '服務費',
        applyToCard: !!values.applyToCard,
        applyToMobile: !!values.applyToMobile
      }

      const res = await api.put(
        `/api/business_B/${businessId}/payment-fee-settings`,
        payload
      )
      if (res?.data?.success) {
        message.success('金流手續費設定已儲存')
      } else {
        message.error(res?.data?.error || '儲存失敗')
      }
    } catch (err) {
      console.error('儲存金流手續費設定失敗:', err)
      message.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          '儲存失敗，請稍後再試'
      )
    } finally {
      setSaving(false)
    }
  }


  return (
    <Card
      title='金流手續費設定'
      style={{ maxWidth: 720, margin: '0 auto' }}
    >
      <Paragraph type='secondary'>
        設定「刷卡」與「行動支付」的預設手續費率。結帳時會依金額 × 費率自動計算手續費，
        店家仍可於每筆訂單單獨調整。手續費會在數據報表中列為
        <Text strong>「銷售費用 - 金流服務費」</Text>
        ，並從營業收入中扣除以計算真實收益。
      </Paragraph>

      <Divider />

      <Spin spinning={loading}>
        <Form
          form={form}
          layout='vertical'
          onFinish={handleSave}
          initialValues={{
            cardFeeRate: 2.8,
            mobilePaymentFeeRate: 2.5,
            autoCalculate: true,
            chargeServiceFee: false,
            feeBearerMode: 'gross_up',
            feeRounding: 'ceil',
            feeLabel: '服務費',
            applyToCard: false,
            applyToMobile: false
          }}
        >
          <Form.Item
            name='cardFeeRate'
            label='刷卡費率（%）'
            tooltip='一般信用卡/簽帳卡收單費率約 1.5% ~ 3%，預設 2.8%；實際依你的收單行合約而定'
            rules={[
              { required: true, message: '請輸入刷卡費率' },
              {
                type: 'number',
                min: 0,
                max: 100,
                message: '費率需介於 0 至 100 之間'
              }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={100}
              step={0.1}
              precision={2}
              addonAfter='%'
              placeholder='例如：2.8'
            />
          </Form.Item>

          <Form.Item
            name='mobilePaymentFeeRate'
            label='行動支付費率（%）'
            tooltip='Line Pay、街口、Apple Pay、悠遊付等第三方支付手續費，約 1% ~ 3%，預設 2.5%；實際依合約而定'
            rules={[
              { required: true, message: '請輸入行動支付費率' },
              {
                type: 'number',
                min: 0,
                max: 100,
                message: '費率需介於 0 至 100 之間'
              }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={100}
              step={0.1}
              precision={2}
              addonAfter='%'
              placeholder='例如：2.5'
            />
          </Form.Item>

          <Form.Item
            name='autoCalculate'
            label='結帳時自動計算手續費'
            valuePropName='checked'
            tooltip='開啟後，當店家於結帳輸入刷卡或行動支付金額，手續費欄位會依費率自動計算'
          >
            <Switch />
          </Form.Item>

          <Divider orientation='left' plain style={{ marginTop: 32 }}>
            客人加收服務費
          </Divider>

          <Paragraph type='secondary' style={{ marginBottom: 8 }}>
            開啟後，客人使用刷卡或行動支付結帳時，會依金額另外加收一筆服務費。
            結帳畫面會直接算好「客人實付」的金額，收據與客人的消費紀錄上也會單獨列出這筆的名目。
          </Paragraph>

          <Form.Item
            name='chargeServiceFee'
            label='向客人加收服務費'
            valuePropName='checked'
            tooltip='這筆服務費不列入店家營收，也不計入設計師的業績與抽成。'
            style={{ marginTop: 20 }}
          >
            <Switch />
          </Form.Item>

          {/* 關閉時整段收起來，避免設定頁被用不到的選項塞滿 */}
          <div style={{ display: chargeServiceFee ? 'block' : 'none' }}>
            <div
              style={{
                background: '#fdf7f9',
                border: '1px solid #f5dfe7',
                borderRadius: 8,
                padding: '16px 16px 4px',
                marginBottom: 16
              }}
            >
              <Form.Item
                name='applyToCard'
                label='刷卡要收服務費'
                valuePropName='checked'
                style={{ marginBottom: 12 }}
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name='applyToMobile'
                label='行動支付要收服務費'
                valuePropName='checked'
                tooltip='Line Pay、街口、Apple Pay 等第三方支付。'
                style={{ marginBottom: 16 }}
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name='feeBearerMode'
                label='計算方式'
                tooltip='銀行是對「客人實際刷的總額」抽成，不是對原本的應收抽成。所以單純把應收乘上費率，那筆多出來的手續費還是會由店家吸收。'
                style={{ marginBottom: 16 }}
              >
                <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <Radio value='gross_up'>
                    <span>補足銀行手續費（建議）</span>
                    <div style={{ fontSize: 12, color: '#8a6b74', lineHeight: 1.6 }}>
                      收滿到「扣掉銀行手續費後剛好等於應收」為止，店家不短收
                    </div>
                  </Radio>
                  <Radio value='add_on'>
                    <span>單純把應收乘上費率</span>
                    <div style={{ fontSize: 12, color: '#8a6b74', lineHeight: 1.6 }}>
                      客人自己乘費率就對得上，但差額仍由店家吸收（金額越大差越多）
                    </div>
                  </Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name='feeRounding'
                label='進位方式'
                style={{ marginBottom: 16 }}
              >
                <Radio.Group>
                  <Radio value='ceil'>無條件進位（建議，不短收）</Radio>
                  <Radio value='round'>四捨五入</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name='feeLabel'
                label='顯示名稱'
                tooltip='會顯示在結帳畫面、收據與客人的消費紀錄上。'
                rules={[{ max: 10, message: '最多 10 個字' }]}
                style={{ marginBottom: 16 }}
              >
                <Input placeholder='服務費' style={{ maxWidth: 240 }} />
              </Form.Item>

              {/* 即時試算：跟結帳頁、後端用的是同一支計算函式 */}
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #f0e0e6',
                  borderRadius: 6,
                  padding: '12px 14px',
                  marginBottom: 16,
                  fontSize: 13,
                  lineHeight: 2
                }}
              >
                <div style={{ fontWeight: 600, color: '#8a4a5c' }}>
                  試算（以應收 NT$1,000 為例）
                </div>
                {wApplyCard && (
                  <div>
                    刷卡：{wLabel || '服務費'} NT${previewCardFee} → 客人實付{' '}
                    <strong>NT${PREVIEW_BASE + previewCardFee}</strong>
                    {'　'}扣銀行手續費後店家實入{' '}
                    <strong>NT${previewCardNet}</strong>
                  </div>
                )}
                {wApplyMobile && (
                  <div>
                    行動支付：{wLabel || '服務費'} NT${previewMobileFee} → 客人實付{' '}
                    <strong>NT${PREVIEW_BASE + previewMobileFee}</strong>
                    {'　'}扣銀行手續費後店家實入{' '}
                    <strong>NT${previewMobileNet}</strong>
                  </div>
                )}
                {!wApplyCard && !wApplyMobile && (
                  <div style={{ color: '#a16207' }}>
                    尚未選擇要收服務費的支付方式
                  </div>
                )}
              </div>

              {enabledButNoTarget && (
                <div style={{ marginBottom: 16 }}>
                  <Hint tone='warning'>
                    已開啟加收服務費，但「刷卡」與「行動支付」都沒有勾選，這個設定不會生效。
                  </Hint>
                </div>
              )}

            </div>
          </div>

          <Form.Item>
            <Button
              type='primary'
              htmlType='submit'
              loading={saving}
              style={{ width: '100%' }}
            >
              儲存設定
            </Button>
          </Form.Item>
        </Form>
      </Spin>

      <Divider dashed />

      <Paragraph type='secondary' style={{ fontSize: 13 }}>
        <Text strong>會計說明：</Text>
        刷卡與行動支付在會計上均屬「非現金」應收帳款，與轉帳並列
        <Text code>非現金</Text>
        收入。收單行於撥款時會代扣手續費，本系統會將已知的手續費列為
        <Text code>銷售費用 - 金流服務費</Text>
        ，並於「營收紀錄查詢」頁面單獨顯示
        <Text strong>「淨營業收入 = 營業收入 − 金流服務費」</Text>
        ，幫助你掌握真實入帳金額與利潤。
      </Paragraph>
    </Card>
  )
}

export default PaymentFeeSettings
